import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useComboSlots, ComboSlot, ComboSlotFormData, PricingRule } from "@/hooks/useComboSlots";
import { ComboSlotFormDialog } from "./ComboSlotFormDialog";
import { Combo } from "@/hooks/useCombos";
import {
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  Loader2,
  Layers,
  Package,
  Tag,
  AlertCircle,
} from "lucide-react";

interface ComboSlotsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  combo: Combo | null;
}

const PRICING_LABELS: Record<PricingRule, string> = {
  highest: 'Maior preço',
  average: 'Média',
  fixed: 'Preço fixo',
  rodizio: 'R$ 0 (Rodízio)',
};

export function ComboSlotsSheet({ open, onOpenChange, combo }: ComboSlotsSheetProps) {
  const [slotFormOpen, setSlotFormOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<ComboSlot | null>(null);
  const [slotToDelete, setSlotToDelete] = useState<ComboSlot | null>(null);

  const { slots, isLoading, createSlot, updateSlot, deleteSlot } = useComboSlots(combo?.id);

  const handleAddSlot = () => {
    setSelectedSlot(null);
    setSlotFormOpen(true);
  };

  const handleEditSlot = (slot: ComboSlot) => {
    setSelectedSlot(slot);
    setSlotFormOpen(true);
  };

  const handleSlotSubmit = async (data: ComboSlotFormData) => {
    if (selectedSlot) {
      await updateSlot.mutateAsync({ slotId: selectedSlot.id, formData: data });
    } else {
      await createSlot.mutateAsync(data);
    }
  };

  const handleDeleteSlot = async () => {
    if (slotToDelete) {
      await deleteSlot.mutateAsync(slotToDelete.id);
      setSlotToDelete(null);
    }
  };

  const formatPrice = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (!combo) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Slots do Combo
            </SheetTitle>
            <SheetDescription>
              {combo.name} - Configure os itens que o cliente pode escolher
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            <Button onClick={handleAddSlot} className="w-full">
              <Plus className="mr-2 h-4 w-4" />
              Adicionar Slot
            </Button>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : slots.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-4 text-lg font-medium">Nenhum slot configurado</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Adicione slots para definir o que o cliente pode escolher neste combo.
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[calc(100vh-280px)]">
                <div className="space-y-3 pr-4">
                  {slots.map((slot, index) => (
                    <div
                      key={slot.id}
                      className="group flex items-start gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <GripVertical className="h-4 w-4 cursor-grab opacity-50" />
                      </div>

                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          {slot.slot_type === 'category' ? (
                            <Layers className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Package className="h-4 w-4 text-green-500" />
                          )}
                          <span className="font-medium">
                            {slot.label || (slot.slot_type === 'category'
                              ? slot.category?.name
                              : slot.product?.name) || `Slot ${index + 1}`}
                          </span>
                          {!slot.is_required && (
                            <Badge variant="outline" className="text-xs">
                              Opcional
                            </Badge>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                          <Badge variant="secondary" className="text-xs">
                            {slot.slot_type === 'category' ? 'Categoria' : 'Produto'}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            Qtd: {slot.min_quantity}-{slot.max_quantity}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            <Tag className="mr-1 h-3 w-3" />
                            {PRICING_LABELS[slot.pricing_rule as PricingRule]}
                            {slot.pricing_rule === 'fixed' && ` (${formatPrice(Number(slot.fixed_price))})`}
                          </Badge>
                        </div>

                        {slot.slot_type === 'category' && slot.category && (
                          <p className="text-xs text-muted-foreground">
                            Categoria: {slot.category.name}
                          </p>
                        )}
                        {slot.slot_type === 'product' && slot.product && (
                          <p className="text-xs text-muted-foreground">
                            Produto: {slot.product.name} - {formatPrice(slot.product.price)}
                          </p>
                        )}
                        {slot.production_location && (
                          <p className="text-xs text-muted-foreground">
                            Imprime em: {slot.production_location.name}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => handleEditSlot(slot)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setSlotToDelete(slot)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ComboSlotFormDialog
        open={slotFormOpen}
        onOpenChange={setSlotFormOpen}
        slot={selectedSlot}
        onSubmit={handleSlotSubmit}
        isPending={createSlot.isPending || updateSlot.isPending}
      />

      <AlertDialog open={!!slotToDelete} onOpenChange={() => setSlotToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Slot</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este slot do combo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSlot}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSlot.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
